import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { TIER_NAMES, type PlanTier } from "../../lib/api";
import { doormanThreadQuery, planQuery } from "../../lib/queries";
import { ForwardIcon } from "../ui/icons";

export const USAGE_HEAD = "Plan usage limits";

/**
 * WHAT THE ONE ALLOWANCE COVERS, and it is both sides of a door (ruling C3).
 * One plan meters the work an owner's Nob does for them AND the work their own
 * knocking asks of somebody else's Nob, so the popover says so rather than
 * leaving a knocker to discover it by watching the ring move.
 */
export const USAGE_BOTH_SIDES =
  "One limit covers both: your own doorman, and your knocking at other doors.";

/**
 * THE MOMENT THE WINDOW RUNS OUT — the roads, never a price (ToU 2.2 §5.3). It
 * said further work drew on credits and pointed at a wallet for the figures;
 * ruling R-0916-1 deleted both, and the paid reset that followed is deleted too
 * (ruling R-0923-3 (1)). What is true now is that the work waits for the next
 * window or a move up. No figure of any kind: consent to the drawdown was given
 * when the plan was bought, and a rate card at the moment of use is the model
 * this product retired.
 */
export const USAGE_OVERFLOW =
  "This month's allowance is used. It resets on its own — or move up a plan.";

/**
 * PAST THE ALLOWANCE AND STILL WORKING (ruling R-0923-3 (8)): pay as you go is
 * on. Where the usage stands, and no figure of money — the dollars are
 * billing's.
 */
export const USAGE_PAST = "Past this month's allowance — pay as you go is on.";

/**
 * How much of the period is gone, drawn as a ring.
 *
 * A RING RATHER THAN A FIGURE, and that is the whole design. A number invites
 * arithmetic — "42% of what?" — and the honest answer to that question is a
 * capacity, which is exactly what ruling C2 keeps server-side. A ring fills. It
 * says "there is room" or "there is not much room" and it cannot be divided
 * back into anything.
 *
 * Open it and the same fact is stated in words, with the two things a share
 * cannot carry on its own: what the allowance covers (both sides of a door) and
 * when the period turns over. There was a third — a credits balance underneath
 * it, shown because a balance was a balance — and ruling R-0916-1 deleted it.
 * What is never shown, here or anywhere, is what the next action will draw.
 *
 * NOTHING AT ALL ON FREE, and that is the whole of ruling C10 rendered. Free
 * performs no AI work, so there is no allowance to have spent: an empty ring
 * there would say "you have used none of your quota" to somebody who has no
 * quota, and a full one would say the opposite lie. `metered` is the fact that
 * decides it — not `used_share === 0`, which is the honest reading for a plan on
 * its first day of the period and must keep drawing an empty track. The upsell
 * that Free DOES owe is the sidebar's own card, said once, above the list.
 *
 * A ring is also withheld while nobody has answered yet. `metered` cannot be
 * guessed from an unanswered read, and a ring that appears and then vanishes on
 * the first Free response is worse than one that arrives a beat late.
 */
export function UsageControl({ onOpenBilling }: { onOpenBilling: () => void }) {
  const plan = useQuery(planQuery());
  const thread = useQuery(doormanThreadQuery());
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const shut = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener("click", shut);
    return () => window.removeEventListener("click", shut);
  }, [open]);

  /**
   * The plan room is the authority; the doorman thread carries the same meter
   * and is the fallback for the one case where the plan read answers nothing —
   * plans dark behind their flag, where the tier is still Free and the share is
   * still true.
   */
  const tier: PlanTier =
    plan.data?.plan.tier ?? (thread.data?.ok ? thread.data.thread.plan.tier : "free");
  const share = plan.data
    ? plan.data.plan.used_share
    : thread.data?.ok
      ? thread.data.thread.plan.used_share
      : 0;
  const overflow = plan.data
    ? plan.data.plan.overflow
    : thread.data?.ok
      ? thread.data.thread.plan.overflow
      : false;
  // Free meters nothing (ruling C10), so it gets no ring and no share — "you
  // have used all of nothing" is a worse sentence than "this tier does no AI
  // work", and only one of them is true. `null` means nobody has said yet.
  const metered: boolean | null = plan.data
    ? plan.data.plan.metered
    : thread.data?.ok
      ? thread.data.thread.plan.metered
      : null;
  // THE STATE, NOT THE DATE. `trial_end` stays set after a trial converts to a
  // paid plan, so reading its presence kept a paying subscriber on "Trial
  // limit" (owner, staging). The plan screen and the billing panel ask the
  // state; the ring asks the same thing.
  const trialing = plan.data?.plan.state === "trialing";
  // THE OPEN ALLOWANCE WINDOW'S END (ruling R-0923-2): a month out on every
  // paid plan, annual included, and the trial's own end on a trial — never the
  // annual renewal, which is the billing period's and a year away.
  const resets = plan.data?.plan.resets_on ?? null;
  // NOT CAPPED AT ONE (ruling R-0923-3 (8)): past the allowance the ring
  // completes and goes on round, and the figure reads past 100%.
  const used = Math.max(share, 0);
  const pct = Math.round(used * 100);
  const days = daysLeft(resets);

  // No quota, or nobody has said yet: no ring, and no control to open one.
  if (metered !== true) return null;

  return (
    <div ref={box} className="relative shrink-0">
      <button
        type="button"
        aria-label={`Plan usage, ${pct}% of this month's allowance used`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="nob-press grid size-9 place-items-center rounded-md hover:bg-hover
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70"
      >
        <Ring used={used} overflow={overflow} />
      </button>

      {open && (
        <div className="nob-usage-pop" role="dialog" aria-label="Plan usage">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onOpenBilling();
            }}
            className="flex w-full items-center justify-between gap-2 px-1 pb-2 text-left text-xs
                       font-medium text-muted transition-colors duration-75 hover:text-text
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70"
          >
            <span>{`${USAGE_HEAD} · ${TIER_NAMES[tier]}`}</span>
            <ForwardIcon className="h-3.5 w-3.5 shrink-0" />
          </button>

          <div className="px-1">
            <p className="flex items-baseline gap-2 text-xs font-semibold">
              {/* "Trial limit" while a trial runs, "Monthly limit" otherwise
                  (§5.1). The trial carries the FULL limit of its tier for its
                  own length, so the only thing that differs is this header. */}
              <span>{trialing ? "Trial limit" : "Monthly limit"}</span>
              {days !== null && (
                <span className="ml-auto font-normal text-muted">
                  {days === 1 ? "Resets in a day" : `Resets in ${days} days`}
                </span>
              )}
              <span className="tabular-nums">{pct}%</span>
            </p>
            <UsageTrack used={used} overflow={overflow} />
          </div>

          {/* Both sides, said in the popover rather than only in the ring: a
              knocker on a plan spends the same limit by knocking (ruling C3). */}
          <p className="mt-2 px-1 text-xs text-muted">{USAGE_BOTH_SIDES}</p>

          {overflow && <p className="mt-2 px-1 text-xs text-muted">{USAGE_OVERFLOW}</p>}
          {!overflow && used > 1 && <p className="mt-2 px-1 text-xs text-muted">{USAGE_PAST}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * How close the period is to gone, as one word the CSS can colour by.
 *
 * ONE function for the track and the ring both, so the bar and the dial can
 * never disagree about where "nearly out" starts. Under four fifths there is
 * no tone at all: a meter that changes colour early is a meter crying wolf.
 */
export function usageTone(used: number, overflow = used >= 1): "near" | "full" | null {
  // "Full" is the wall — nothing left to work with — and that is the server's
  // `overflow`, not a share: with pay as you go on, 100% is not a wall
  // (docs/plans.md §5.4), and a red ring over work that is still running would
  // be a false alarm.
  if (overflow) return "full";
  if (used > 1) return null;
  return used > 0.8 ? "near" : null;
}

/**
 * HOW MUCH OF A BAR THE PART BEYOND THE ALLOWANCE TAKES: its TRUE SHARE of
 * everything used (owner, 2026-09-24). Past 100% the bar is the whole of the
 * usage, the allowance is `1/used` of it and pay as you go `(used − 1)/used` —
 * at 180% the second segment is 80/180 of the bar. It replaces a curve that
 * never passed three tenths of the width (2026-09-23), which drew 110% and
 * 300% at nearly the same size. 0 up to 100%. The bars only: the ring keeps
 * its lap per 100%.
 */
export function beyondShare(used: number): number {
  if (used <= 1) return 0;
  return (used - 1) / used;
}

function toneAttr(used: number, overflow?: boolean): { "data-tone"?: "near" | "full" } {
  const tone = usageTone(used, overflow ?? used >= 1);
  return tone ? { "data-tone": tone } : {};
}

/**
 * The popover's bar: the allowance, and past it a second segment in its own
 * colour. The same two numbers the billing panel's bar draws.
 */
export function UsageTrack({ used, overflow }: { used: number; overflow: boolean }) {
  const beyond = beyondShare(used);
  if (beyond === 0) {
    return (
      <div className="nob-usage-track">
        <i style={{ inlineSize: `${Math.round(used * 100)}%` }} {...toneAttr(used, overflow)} />
      </div>
    );
  }
  return (
    <div className="nob-usage-track flex" data-past-allowance>
      <i style={{ inlineSize: `${(1 - beyond) * 100}%` }} {...toneAttr(used, overflow)} />
      <b style={{ inlineSize: `${beyond * 100}%` }} data-beyond />
    </div>
  );
}

/** Whole days until the period turns over. `null` when the server said nothing. */
export function daysLeft(iso: string | null, now: number = Date.now()): number | null {
  if (!iso) return null;
  const at = Date.parse(iso);
  if (!Number.isFinite(at)) return null;
  const days = Math.ceil((at - now) / 86_400_000);
  return days > 0 ? days : null;
}

function Ring({ used, overflow }: { used: number; overflow: boolean }) {
  const r = 8;
  const circumference = 2 * Math.PI * r;
  const tone = usageTone(used, overflow);
  // Past 100% the ring is complete, and the part beyond goes round again in
  // its own colour — a whole second lap at double the allowance, and no more.
  const beyond = Math.min(Math.max(used - 1, 0), 1);
  return (
    <span className="relative grid place-items-center">
      <svg
        viewBox="0 0 22 22"
        aria-hidden
        focusable="false"
        className="h-[22px] w-[22px] -rotate-90"
      >
        {/* The EMPTY TRACK, and it has to be legible at nought per cent in
            every room — this is the whole of what a plan on the first day of
            its period looks like. `border` rather than `border-strong` is the
            reference build's own rung: a hairline in the bright rooms, and the
            skin's ink on the yellow column, where a softer grey would sink. */}
        <circle cx="11" cy="11" r={r} fill="none" strokeWidth="2.5" className="stroke-border" />
        <circle
          cx="11"
          cy="11"
          r={r}
          fill="none"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - Math.min(used, 1))}
          className={
            tone === "full" ? "stroke-danger" : tone === "near" ? "stroke-warn" : "stroke-text"
          }
        />
        {beyond > 0 && (
          <circle
            cx="11"
            cy="11"
            r={r}
            fill="none"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - beyond)}
            className="stroke-brass"
            data-beyond
          />
        )}
      </svg>
      {/* A dot sat here, in the corner of the ring, for a balance underneath
          the allowance — a different kind of money that did not reset. Ruling
          R-0916-1 deleted it, and there is exactly one thing left to measure. */}
    </span>
  );
}
